import { useState } from 'react'
import { Lightbulb } from 'lucide-react'
import { Input } from '@/components/ui/Input'
import { cn } from '@/components/ui/cn'

const HINT_SEPARATOR = '\n\n'

interface FillBlankQuestionProps {
  questionText: string
  options?: string[]
  value: string
  onChange: (value: string) => void
  onSubmit?: () => void
  disabled?: boolean
}

function splitQuestionText(questionText: string) {
  const separatorIndex = questionText.indexOf(HINT_SEPARATOR)
  if (separatorIndex === -1) {
    return { sentence: questionText, hint: null }
  }
  return {
    sentence: questionText.slice(0, separatorIndex),
    hint: questionText.slice(separatorIndex + HINT_SEPARATOR.length),
  }
}

function HintToggle({ hint }: { hint: string }) {
  const [showHint, setShowHint] = useState(false)

  return (
    <div>
      {showHint ? (
        <div className="space-y-2">
          <p className="rounded-lg border border-primary/20 bg-primary/5 px-4 py-3 text-sm text-foreground">
            {hint}
          </p>
          <button
            type="button"
            onClick={() => setShowHint(false)}
            className="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
          >
            Hide hint
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setShowHint(true)}
          className="inline-flex items-center gap-2 rounded-lg border border-dashed border-border px-3 py-2 text-sm text-muted-foreground transition-colors hover:border-primary/40 hover:bg-secondary/40 hover:text-foreground"
        >
          <Lightbulb className="h-4 w-4" />
          Need a hint?
        </button>
      )}
    </div>
  )
}

export function FillBlankQuestion({
  questionText,
  options,
  value,
  onChange,
  onSubmit,
  disabled = false,
}: FillBlankQuestionProps) {
  const { sentence, hint } = splitQuestionText(questionText)

  if (options && options.length > 0) {
    return (
      <div className="space-y-4">
        <div className="space-y-3">
          <p className="text-base leading-relaxed text-foreground sm:text-xl">{sentence}</p>
          {hint ? <HintToggle key={questionText} hint={hint} /> : null}
        </div>
        <div className="grid gap-2">
          {options.map((option, index) => (
            <button
              key={option}
              type="button"
              disabled={disabled}
              onClick={() => onChange(option)}
              className={cn(
                'flex min-h-12 items-center gap-3 rounded-xl border px-4 py-3.5 text-left text-sm transition-colors',
                value === option
                  ? 'border-primary bg-primary/10 text-foreground ring-1 ring-primary/30'
                  : 'border-border bg-card text-foreground hover:border-primary/40 hover:bg-secondary/60'
              )}
            >
              <span
                className={cn(
                  'flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-xs font-semibold',
                  value === option
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-secondary text-muted-foreground'
                )}
              >
                {String.fromCharCode(65 + index)}
              </span>
              <span>{option}</span>
            </button>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <p className="text-lg leading-relaxed text-foreground sm:text-xl">{sentence}</p>
        {hint ? <HintToggle key={questionText} hint={hint} /> : null}
      </div>
      <Input
        placeholder="Type the missing word"
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault()
            onSubmit?.()
          }
        }}
        className="min-h-12 text-base"
        data-testid="fill-blank-input"
      />
    </div>
  )
}
